import React, { useState } from 'react';
import Axios from '../../utils/axios';
import { Option } from '../../components/Atomos/DropDown/DDMultiple';
import CatalogTableC from '../../components/Catalogs/CatalogTableC';
import PaginatedTable from '../../components/Organismos/Table/PaginatedTable';
import useDataLoader2 from '../../hooks/useDataLoader2';
import { formatTimeSpanToString } from '../../utils/functions';
import { defaultPageSize } from '../../utils/const';
import Filters, { IFilterData } from '../../components/Catalogs/Filters/Filters';
import { UseFirmList } from '../../hooks/Catalogs/useFirm';
import { MemberTypeString, Title } from '../../Enums/PersonEnums';
import { IPerson, memberTypeOptions } from '../../interfaces';
import PersistentLink from '../../components/Moleculas/PersistentLink';
import ShapeEdit from '../../components/Atomos/Icons/ShapeEdit';
import { exportAllDataLoader2 } from "../../utils/exportAllDataLoader2";

const Person: React.FC = ({ ...props }) => {      
  const axios = new Axios();
  const { firmData } = UseFirmList();
  const endpoint = '/person';
  const {
    data,
    totalCount,
    fetchData,
    setFilter,
    sort,
    setSort,
    pagination,
    setPagination,
    inputFilters,
    setInputFilters,
    filter,
  } = useDataLoader2<any>({
    endpoint,
    defaultPageSize,
    defaultSorting: { field: "name", order: "asc" },
  });

  const columnsDef = React.useMemo(() => [
    {
      id: "count",
      header: "#",
      enableSorting: false,
      cell: ({ rowIndex }: { rowIndex: number; row: any; value: any }) => (
        <p> {rowIndex + 1 + pagination.pageIndex * pagination.pageSize} </p>
      ),
    },
    { id: 'generatedId', header: 'Generated ID' },
    { id: 'memberType', header: 'Member Type' },
    { id: 'isAttorney', header: 'Is Attorney' },
    { id: 'firm', header: 'Firm' },
    {
      id: "photo",
      header: "Photo",
      cell: ({ value }: { rowIndex: number; row: any; value: any }) => (
        <div
          className={`p-4 text-left not-italic text-gray-font font-normal !text-[0.875rem] lg:!text-base !leading-4 lg:!leading-[1.5rem] relative`}
        >
          <img
            className="w-10 h-10 text-center rounded-full"
            src={value}
            alt="avatar"
          />
        </div>
      ),
    },
    { id: 'name', header: 'Name' },
    { id: 'middleInitial', header: 'Middle Initial' },
    { id: 'lastName', header: 'Last Name' },
    { id: 'title', header: 'Title' },
    { id: 'unlistedTitle', header: 'Unlisted Title' },
    { id: 'listTitle', header: 'List Title' },
    { id: 'listingCategory', header: 'Listing Category' },
    { id: 'isSearchable', header: 'Is Searchable' },
    { id: 'firmEmail', header: 'Firm Email' },
    { id: 'mainOffice', header: 'Main Office' },
    { id: 'createdDate', header: "Created Date" },
    {
      id: 'edit', 
      header: 'Edit', 
      enableSorting: false,
      cell: ({ row, rowIndex }: { rowIndex: number; row: any; value: any }) => (
        <PersistentLink
          className="edit"
          to={`Edit/${row.id}`}
          state={data[rowIndex]}
          persistingData={{
            pagination,
            sort,
            filter, 
            inputFilters, 
          }}
        >
          Edit
          <div className="editsvg"><ShapeEdit /></div>
        </PersistentLink>
      )
    },
  ], [pagination.pageIndex, pagination.pageSize, sort, filter, inputFilters, data]);

  const formatData = (data: IPerson[]) => {
    const formatedData = data.map(d => {
      return {
        ...d,
        memberType: MemberTypeString[d.memberType],
        isAttorney: d.isAttorney ? 'Yes' : 'No',
        firm: d.firmName, 
        isSearchable: d.isSearchable ? 'Yes' : 'No',
        mainOffice: d.mainOfficeName,
        createdDate: d.createdDate ? formatTimeSpanToString(d.createdDate) : ''
      }
    })
    return formatedData
  }

  const [isAttorneyOptions] = useState<Option[]>([{ id: 'No', name: 'No' }, { id: 'Yes', name: 'Yes' }]);
  const enumTitles = Object.entries(Title).map(([key, value]) => ({ id: key, name: value } as Option));
  const [titleOptions] = useState<Option[]>(enumTitles);
  const [isMemberOptions] = useState<Option[]>(memberTypeOptions);
  const filtersData: IFilterData[] = React.useMemo(() => [
    {
      name: "Name",
      type: "text",
      comparison: "CONTAINS",
    },
    {
      name: "Firm",
      objectData: firmData?.map((f: any) => ({id:f.id, name: f.name})) || [],
      comparison: 'EQUAL'
    },
    {
      name: "Title",
      objectData: titleOptions?.map(r => ({id: r.id, name: r.name})) || [],
      comparison: 'BY_NAME',
      disabledWrite: true,
    },
    {
      name: "Person Type", 
      fieldName: "memberType",
      objectData: isMemberOptions?.map(c => ({id: c.id, name: c.name})) || [],
      disabledWrite: true,
    },
    {
      name: "Is Attorney",
      fieldName: "states",
      objectData: isAttorneyOptions || [],
      disabledWrite: true,
    },
  ], [firmData]);

  return (
    <>
      <div>
        <CatalogTableC
          route={'Person'}
          catalogName='PERSONS'
          catalogShortName='PERSON'
          title={`Person Catalog`}
          totalCount={totalCount}
        >
          <Filters
            filtersData={filtersData} 
            filterValues={inputFilters}
            setFilterValues={setInputFilters} 
            Search={() => fetchData()}
            clearSelections={() => fetchData(true)}
            setValueForQuery={setFilter}
          />
          <PaginatedTable
            data={data || []}
            columnsDef={columnsDef}
            formatData={formatData}
            sorting={sort}
            onSortingChange={setSort}
            totalCount={totalCount || 0}
            pagination={pagination}
            onPaginationChange={setPagination}
            endpoint={endpoint}
            onExportAll={() =>
              exportAllDataLoader2<any>({
                endpoint,
                filter,
                sort,
                pageSize: 1000,
              })
            }
          />
        </CatalogTableC>
      </div>
    </>
  );
};

export default Person;